/**
 * createSnapshot — convert a live PipelineBatchResult into a compact,
 * serializable PipelineSnapshot. Pure function, no I/O.
 *
 * Lossy by design — see snapshots/types.ts for the rationale.
 */

import type { PipelineBatchResult } from '../issueContract'
import { deriveDisplaySeverity, type SeverityLevel } from '../../utils/severity'
import { computeCodeFrequencyReport } from '../utils/codeFrequency'
import { resolveModuleCodeForSnapshot } from './resolveSnapshotModule'
import type {
  PipelineSnapshot,
  SnapshotAggregate,
  SnapshotQuestionRow,
  SnapshotRunConfig,
} from './types'

interface CreateSnapshotOptions {
  label?: string
  note?: string
  config?: SnapshotRunConfig
  /** Override id generation (useful for tests). */
  id?: string
}

function generateId(): string {
  const ts = Date.now()
  const rnd = Math.random().toString(36).slice(2, 8)
  return `snap_${ts}_${rnd}`
}

/**
 * Build a PipelineSnapshot from a batch result.
 *
 * Per-question rows store signatures (deduplicated by the pipeline) so two
 * snapshots can be compared without ambiguity about "same issue" vs
 * "different issue".
 */
export function createSnapshot(
  batchResult: PipelineBatchResult,
  options: CreateSnapshotOptions = {},
): PipelineSnapshot {
  const severityCounts: Record<SeverityLevel, number> = {
    critical: 0,
    blocker: 0,
    review: 0,
    coach: 0,
    info: 0,
  }

  const rows: SnapshotQuestionRow[] = batchResult.results.map((r) => {
    const kinds = { blocker: 0, coach: 0, info: 0 } as Record<
      'blocker' | 'coach' | 'info',
      number
    >
    const codeSet = new Set<string>()
    for (const issue of r.gate.issues) {
      kinds[issue.kind] += 1
      codeSet.add(issue.code)
      const ds = deriveDisplaySeverity(issue.kind, issue.severity)
      severityCounts[ds] += 1
    }
    return {
      qid: r.questionId,
      fid: r.familyId,
      mod: resolveModuleCodeForSnapshot(r.questionId, r.familyId),
      v: r.gate.verdict,
      s: r.gate.score,
      kinds,
      codes: Array.from(codeSet).sort(),
    }
  })

  let totalIssues = 0
  for (const row of rows) {
    totalIssues += row.kinds.blocker + row.kinds.coach + row.kinds.info
  }

  const codeFrequency = computeCodeFrequencyReport(batchResult, { topN: 50 })

  const aggregate: SnapshotAggregate = {
    totalQuestions: batchResult.totalQuestions,
    totalIssues,
    verdicts: { ...batchResult.verdicts },
    severityCounts,
    issuesByKind: { ...batchResult.issuesByKind },
    issuesByDomain: { ...batchResult.issuesByDomain },
    avgScore: batchResult.avgScore,
  }

  return {
    schemaVersion: 1,
    id: options.id ?? generateId(),
    createdAt: new Date().toISOString(),
    ...(options.label !== undefined ? { label: options.label } : {}),
    ...(options.note !== undefined ? { note: options.note } : {}),
    config: options.config ?? {},
    aggregate,
    rows,
    codeFrequency: {
      totalIssues: codeFrequency.totalIssues,
      uniqueCodes: codeFrequency.uniqueCodes,
      entries: codeFrequency.entries,
      byKind: codeFrequency.byKind,
      byDomain: codeFrequency.byDomain,
    },
  }
}
